/**
 * Checkout panel state and validation: open/close, errors, visibility.
 */
import { useCallback } from "react";
import { get } from "lodash";
import {
  CHECKOUT_PANELS as PANELS,
  CHECKOUT_PANELS_ORDER as PANELS_ORDER,
  shouldCollectShipAddress,
} from "../components/shop/helpers";
import type {
  CheckoutState,
  CheckoutPanelState,
} from "../components/shop/Checkout/types";
import { checkoutAdditionalInfoHasErrors } from "../components/shop/Checkout/checkoutAdditionalInfo";
import type { PublicViewContextValue } from "./useCheckoutState";

interface UseCheckoutPanelsArgs {
  state: CheckoutState;
  setState: React.Dispatch<React.SetStateAction<CheckoutState>>;
  context: PublicViewContextValue | undefined;
  entityFromContext: Record<string, unknown> | undefined;
  checkAddressErrors: (field: string) => boolean;
  checkContactErrors: (field: string) => boolean;
}

export function useCheckoutPanels({
  state,
  setState,
  context,
  entityFromContext,
  checkAddressErrors,
  checkContactErrors,
}: UseCheckoutPanelsArgs) {
  const checkCompanyNameErrors = useCallback((): boolean => {
    return !state.billing_address.address_company.trim();
  }, [state.billing_address.address_company]);

  const checkAdditionalInfoErrors = useCallback((): boolean => {
    return checkoutAdditionalInfoHasErrors(
      entityFromContext,
      state.customer_po,
      state.order_metadata,
    );
  }, [entityFromContext, state.customer_po, state.order_metadata]);

  const isShopAggregate = useCallback((): boolean => {
    return Number(context?.publicViewEntity?.entityAggregate) === 1;
  }, [context?.publicViewEntity?.entityAggregate]);

  const checkPanelErrors = useCallback(
    (panel: string): boolean => {
      switch (panel) {
        case PANELS.COMPANY_NAME:
          return checkCompanyNameErrors();
        case PANELS.BILLING_INFO:
          return (
            checkAddressErrors("billing_address") ||
            checkContactErrors("billing_contact")
          );
        case PANELS.SHIPPING_INFO: {
          if (
            shouldCollectShipAddress(entityFromContext) &&
            isShopAggregate()
          ) {
            return checkAddressErrors("shipping_address");
          }
          return (
            checkAddressErrors("shipping_address") ||
            checkContactErrors("shipping_contact")
          );
        }
        case PANELS.CUSTOMER_INFO:
          return checkContactErrors("order_contact");
        case PANELS.ADDITIONAL_INFO:
          return checkAdditionalInfoErrors();
        case PANELS.COMMENTS:
        default:
          return false;
      }
    },
    [
      entityFromContext,
      checkCompanyNameErrors,
      checkAddressErrors,
      checkContactErrors,
      checkAdditionalInfoErrors,
      isShopAggregate,
    ],
  );

  const showCompanyName = (): boolean => {
    const entityClientId = context?.publicViewEntity?.entityClientId;
    return !(
      !!entityClientId || entityFromContext?.billing_address_type !== "TYPED"
    );
  };

  const showMetadataList = useCallback((): boolean => {
    const buyInventory = context?.publicViewEntity?.buyInventory;
    return !!(
      entityFromContext &&
      !buyInventory &&
      (Object.keys(
        (entityFromContext.metadata_list as Record<string, unknown>) ?? {},
      ).length > 0 ||
        entityFromContext.collect_customer_po !== "NO")
    );
  }, [context?.publicViewEntity?.buyInventory, entityFromContext]);

  const showComments = (): boolean => !context?.publicViewEntity?.buyInventory;

  const toggleOpenPanel = useCallback(
    (panel: string) => {
      const hasErrors = checkPanelErrors(panel);
      const showAddInfo = showMetadataList();
      let panelNum = PANELS_ORDER[panel as keyof typeof PANELS_ORDER];
      panelNum = panelNum === 3 ? 4 : panelNum;
      panelNum = panelNum === 4 && !showAddInfo ? 5 : panelNum;

      let nextPanelNum = panelNum ? panelNum + 1 : null;
      if (
        shouldCollectShipAddress(entityFromContext) &&
        isShopAggregate() &&
        state.openPanel === PANELS.CUSTOMER_INFO
      ) {
        nextPanelNum = 3;
      }

      const nextPanel =
        (panelNum
          ? Object.keys(PANELS_ORDER).find(
              (k) =>
                PANELS_ORDER[k as keyof typeof PANELS_ORDER] === nextPanelNum,
            )
          : "") ?? "";

      const openPanel =
        panel === state.openPanel && !hasErrors ? nextPanel : panel;
      const newState: Partial<CheckoutState> = { openPanel };
      if (
        openPanel !== panel &&
        get(
          state[`PANEL_${state.openPanel}` as keyof CheckoutState] as Record<
            string,
            unknown
          >,
          "opened",
          false,
        )
      ) {
        newState.openPanel = "";
      }
      const currentPanelKey = `PANEL_${state.openPanel}` as keyof CheckoutState;
      const currentPanel = state[currentPanelKey] as CheckoutPanelState;
      (newState as Record<string, unknown>)[currentPanelKey] = {
        ...currentPanel,
        showPreview: true,
        showCheckmark: true,
        showEditBtn: true,
        opened: true,
      };
      if (panel === state.openPanel && hasErrors) {
        (
          (newState as Record<string, unknown>)[currentPanelKey] as {
            showErrors: boolean;
          }
        ).showErrors = true;
      }

      setState((s) => ({ ...s, ...newState }));
    },
    [
      checkPanelErrors,
      state,
      entityFromContext,
      setState,
      showMetadataList,
      isShopAggregate,
    ],
  );

  const isPanelOpen = (panel: string) => state.openPanel === panel;

  return {
    checkCompanyNameErrors,
    checkPanelErrors,
    isShopAggregate,
    toggleOpenPanel,
    isPanelOpen,
    showCompanyName,
    showMetadataList,
    showComments,
  };
}
